import * as z from "zod";
import { code, flag, pagination, uuidV7, version } from "../index";

// Sales campaigns (50-api/booking.md §2.1): what the brands are invited to and the customers book appointments for.

const name = z.string().trim().min(1).max(200);

/**
 * A link a customer opens from the page of the booking (spec of the Sales team §3, ADR-312): `https:` only, since the
 * page shows it as it is.
 */
export const campaignLinkUrl = z.url({ protocol: /^https$/, normalize: true }).max(2000);

/** `null` empties a field. */
const campaignFields = z.object({
  season: code.nullable(),
  category: code.nullable(),
  showroomLocation: code.nullable(),
  type: z.string().trim().min(1).max(60).nullable(),
  year: z.number().int().min(2000).max(2100).nullable(),
  startDate: z.iso.date().nullable(),
  endDate: z.iso.date().nullable(),
  /** The Portfolio and the Flyer of the campaign, which the page of the booking links (ADR-312). */
  portfolioUrl: campaignLinkUrl.nullable(),
  flyerUrl: campaignLinkUrl.nullable(),
});

/** The campaign that starts last first; without `archived`, the ones not archived. */
export const listSalesCampaignsInput = z.object({
  /** Part of the name, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Code of the season. */
  season: code.optional(),
  /** Only the archived ones instead of the others. */
  archived: flag.optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListSalesCampaignsInput = z.input<typeof listSalesCampaignsInput>;

export const getSalesCampaignInput = z.object({ salesCampaignId: z.uuid() });
export type GetSalesCampaignInput = z.input<typeof getSalesCampaignInput>;

/** The id comes from the client (ADR-046): a retry by the same user returns the campaign already created. */
export const createSalesCampaignInput = campaignFields.partial().extend({ id: uuidV7, name });
export type CreateSalesCampaignInput = z.input<typeof createSalesCampaignInput>;

/** PATCH: an absent field stays as it is, `null` empties it. */
export const updateSalesCampaignInput = campaignFields
  .partial()
  .extend({ salesCampaignId: z.uuid(), version, name: name.optional() });
export type UpdateSalesCampaignInput = z.input<typeof updateSalesCampaignInput>;
export const updateSalesCampaignBody = updateSalesCampaignInput.omit({ salesCampaignId: true });

/** An action on a campaign, with the version the client read. */
export const salesCampaignActionInput = z.object({ salesCampaignId: z.uuid(), version });
export type SalesCampaignActionInput = z.input<typeof salesCampaignActionInput>;
/** The gateway routes split the input: the id in the path, the rest in the body. */
export const salesCampaignParams = salesCampaignActionInput.pick({ salesCampaignId: true });
export const salesCampaignVersionBody = salesCampaignActionInput.pick({ version: true });

export const salesCampaignDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    season: z.string().nullable(),
    category: z.string().nullable(),
    showroomLocation: z.string().nullable(),
    type: z.string().nullable(),
    year: z.number().int().nullable(),
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
    portfolioUrl: z.string().nullable(),
    flyerUrl: z.string().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    /** Brands with at least one availability in the campaign. */
    brandCount: z.number().int(),
    version: z.number().int(),
  })
  .meta({ id: "SalesCampaign" });
